import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../index';
import { calculateCombinedPerformance, calculateSectionFuelRequirements } from '../../utils/calculations/breakawayCalculations';
import { validateBreakawayHull } from '../../utils/validation/breakawayValidation';

// Basic selectors
export const selectBreakawayHull = (state: RootState) => state.breakawayHull;
export const selectIsBreakaway = (state: RootState) => state.breakawayHull.isBreakaway;
export const selectBreakawaySections = (state: RootState) => state.breakawayHull.sections;
export const selectCombinedPerformance = (state: RootState) => state.breakawayHull.combinedPerformance;
export const selectSections = (state: RootState) => state.breakawayHull.sections;
export const selectTotalTonnage = (state: RootState) => state.breakawayHull.totalTonnage;

// Section selectors
export const selectSectionById = (state: RootState, sectionId: string) => 
  state.breakawayHull.sections.find(section => section.id === sectionId);

export const selectActiveSection = (state: RootState, activeSectionId: string) => 
  state.breakawayHull.sections.find(section => section.id === activeSectionId);

// Combined performance selector with calculations
export const selectCalculatedCombinedPerformance = createSelector(
  [selectBreakawaySections],
  (sections) => {
    if (sections.length === 0) {
      return {
        maneuver: 0,
        jump: 0,
        reaction: 0,
        totalPower: 0,
        totalFuel: 0,
        totalCost: 0,
      };
    }
    return calculateCombinedPerformance(sections);
  }
);

// Section fuel requirements selector
export const selectSectionFuelRequirements = createSelector(
  [selectSectionById],
  (section) => {
    if (!section) return null;
    return calculateSectionFuelRequirements(section);
  }
);

// All sections fuel requirements
export const selectAllSectionsFuelRequirements = createSelector(
  [selectBreakawaySections],
  (sections) => {
    return sections.map(section => ({
      sectionId: section.id,
      sectionName: section.name,
      requirements: calculateSectionFuelRequirements(section),
      allocation: section.fuelAllocation,
    }));
  }
);

// Total fuel requirements across all sections
export const selectTotalFuelRequirements = createSelector(
  [selectAllSectionsFuelRequirements],
  (sectionsFuel) => {
    return sectionsFuel.reduce((total, section) => {
      return {
        reactionDrive: total.reactionDrive + section.requirements.reactionDrive,
        jumpDrive: total.jumpDrive + section.requirements.jumpDrive,
        powerPlant: total.powerPlant + section.requirements.powerPlant,
        total: total.total + section.requirements.total,
      };
    }, { reactionDrive: 0, jumpDrive: 0, powerPlant: 0, total: 0 });
  }
);

// Section validation selector
export const selectSectionValidation = createSelector(
  [selectSectionById],
  (section) => {
    if (!section) return { isValid: false, errors: [], warnings: [] };

    const errors: string[] = [];
    const warnings: string[] = [];

    // Required systems validation
    if (!section.bridge) {
      errors.push(`${section.name} must have a bridge`);
    }
    
    if (!section.powerPlant) {
      errors.push(`${section.name} must have a power plant`);
    }

    // Optional systems validation
    if (!section.drives.maneuver && !section.drives.jump && !section.drives.reaction) {
      warnings.push(`${section.name} has no drives installed - it cannot move independently`);
    }

    // Fuel validation
    if (section.fuelAllocation.tons < section.fuelAllocation.requirements.total) {
      errors.push(`${section.name} has insufficient fuel (${section.fuelAllocation.tons} tons vs ${section.fuelAllocation.requirements.total} tons required)`);
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }
);

// Overall breakaway hull validation
export const selectBreakawayHullValidation = createSelector(
  [selectBreakawaySections],
  (sections) => {
    const allErrors: string[] = [];
    const allWarnings: string[] = [];

    sections.forEach(section => {
      const validation = selectSectionValidation({ breakawayHull: { sections: [section] } } as RootState, section.id);
      allErrors.push(...validation.errors);
      allWarnings.push(...validation.warnings);
    });

    return {
      isValid: allErrors.length === 0,
      errors: allErrors,
      warnings: allWarnings,
    };
  }
);

// Section configuration status
export const selectSectionConfigurationStatus = createSelector(
  [selectSectionById],
  (section) => {
    if (!section) return { isComplete: false, missingSystems: [] };

    const missingSystems: string[] = [];

    if (!section.bridge) missingSystems.push('Bridge');
    if (!section.powerPlant) missingSystems.push('Power Plant');

    return {
      isComplete: missingSystems.length === 0,
      missingSystems,
      hasDrives: !!(section.drives.maneuver || section.drives.jump || section.drives.reaction),
      hasSensors: !!section.sensors,
      hasWeapons: section.weapons.length > 0,
      hasArmor: !!section.armor,
    };
  }
);

// Comprehensive validation selector
export const selectBreakawayValidation = createSelector(
  [selectBreakawayHull],
  (breakawayHull) => {
    return validateBreakawayHull(breakawayHull);
  }
); 